import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, useMediaQuery, useTheme, Divider } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useState } from 'react';
import logo from '../assets/Samohealth-Pharma-Logo-Web.png';
import { supabase } from '../lib/supabase';

const drawerWidth = 240;

const Layout = () => {
  const navigate = useNavigate();
  const { user, setUser } = useApp();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Accounts Details', icon: <PeopleIcon />, path: '/accounts' },
    { text: 'Client Management', icon: <PersonIcon />, path: '/clients' },
    { text: 'Logs', icon: <HistoryIcon />, path: '/logs' },
    { text: 'Profile', icon: <AccountCircleIcon />, path: '/profile' },
  ];

  const handleLogout = async () => {
    try {
      // First sign out from supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Then clear the user from context
      setUser(null);
      
      // Finally navigate to login
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      // Still try to clear user state and redirect even if there's an error
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const drawer = (
    <>
      <Toolbar />
      <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <List>
          {menuItems.map((item) => (
            <ListItem 
              button 
              key={item.text} 
              onClick={() => handleNavigation(item.path)}
              sx={{
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        <Divider />
        <List>
          <ListItem 
            button 
            onClick={handleLogout}
            sx={{
              '&:hover': {
                backgroundColor: '#f5f5f5',
              }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'black',
          width: '100%',
          ...((!isMobile && drawerOpen) && {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: `${drawerWidth}px`,
          }),
          transition: 'width 0.3s, margin-left 0.3s',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src={logo}
            alt="Samohealth Logo"
            sx={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'left center',
              maxWidth: '180px',
              display: 'block',
              my: 1,
              flexShrink: 0,
              mr: 'auto'
            }}
          />
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: 'white',
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        // Desktop Drawer
        <Drawer
          variant="permanent"
          open={drawerOpen}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: 'white',
              transform: drawerOpen ? 'none' : 'translateX(-100%)',
              transition: 'transform 0.3s'
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          p: 3,
          width: '100%',
          ...((!isMobile && drawerOpen) && {
            width: `calc(100% - ${drawerWidth}px)`,
          }),
          transition: 'width 0.3s'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout; 